import React, { useCallback, useMemo, useState } from 'react';
import { FieldFilterBoxSelectExpression } from '@components/filters/FieldFilterBoxSelectExpression';
import {
  Filter,
  filterOperatorOptions,
  filterToRawExpression,
  isTopKFilter,
  MultiSelectTags,
  OptionObject,
  SortDir,
  TextInput,
} from '@fabric-ui/components';
import { ArrowDown } from '@fabric-ui/icons';
import { ellipsis } from '@fabric-ui/theme';
import { noop } from '@fabric-ui/utils';
import { FINDING_STATE_LABELS } from '@utils/filterValueLabels';
import { FieldFilterBase } from '../FieldFilterBase';
import TopKDisplay from './components/TopKDisplay';
import { useFilterConfig, useFilterContext } from './FilterContext';

interface FieldFilterBoxSelectProps {
  fieldName?: string | null;
  value: Filter;
  onChange: (filter: Filter) => void;
  extraOptions?: string[];
  disabled?: boolean;
}

export const FieldFilterBoxSelect = ({ value, onChange, fieldName, extraOptions, disabled = false }: FieldFilterBoxSelectProps) => {
  const { size, isVertical } = useFilterConfig();
  const { measurements } = useFilterContext();
  const [isOpen, setIsOpen] = useState(false);
  const rawExpression = useMemo(() => filterToRawExpression(value), [value]);

  const filters = useMemo(() => {
    const stringCondition = value.expression?.stringCondition;
    if (stringCondition?.in !== undefined) {
      const values = stringCondition.in.values ?? [];
      return stringCondition.addNullFilter ? [...values, null] : values;
    }
    return rawExpression ? [encodeURIComponent(JSON.stringify(rawExpression))] : [];
  }, [value, rawExpression]);

  const stateLabels = fieldName === 'finding.state' ? FINDING_STATE_LABELS : undefined;
  const selectedFilterOptions: OptionObject<string>[] = useMemo(
    () => filters.map(f => ({ label: (f && stateLabels?.[f]) || f || 'Empty', value: f ?? undefined })),
    [filters, stateLabels]
  );

  const filterValueLabel = useMemo(() => {
    if (rawExpression && rawExpression?.and?.[0]?.operator !== 'in') {
      if (rawExpression.type && isTopKFilter(rawExpression.type)) {
        const { top, dir, value: metric } = rawExpression;
        if (!top || !metric) return 'Filter value';
        const dirLabel = dir === SortDir.DESC ? 'Top' : 'Bottom';
        const metricName = measurements.asObject[metric]?.displayName || metric;
        return `Filter value: ${dirLabel} ${top} by ${metricName}`;
      }
      const operator = rawExpression.and ? 'and' : (rawExpression as { or?: unknown }).or ? 'or' : null;
      if (operator) {
        const operands: { operator: string; value: string }[] = rawExpression[operator];
        const parts = operands.map(({ operator: op, value: v }) => {
          const label = filterOperatorOptions.string.find(({ value: val }) => val === op)?.title ?? op;
          return `${label} "${v}"`;
        });
        return `Filter value: ${parts.join(` ${operator.toUpperCase()} `)}`;
      }
    }
    if (selectedFilterOptions.length > 0) {
      return `Filter value: ${selectedFilterOptions.map(o => o.label).join(', ')}`;
    }
    return 'Filter value';
  }, [rawExpression, selectedFilterOptions, measurements]);

  const handleTagDelete = useCallback(
    (index: number) => {
      if (value.expression?.stringCondition?.in !== undefined) {
        const remaining = filters.filter((_, i) => i !== index);
        const newAddNullFilter = remaining.includes(null);
        const newNonNullValues = remaining.filter(v => v !== null);
        const hasValues = newNonNullValues.length > 0 || newAddNullFilter;
        onChange({
          ...value,
          expression: {
            ...value.expression,
            stringCondition: hasValues
              ? { in: { values: newNonNullValues }, ...(newAddNullFilter && { addNullFilter: true }) }
              : { contains: '' },
          },
        });
      }
    },
    [filters, value, fieldName]
  );

  const getTagProps = useCallback(
    ({ index }: { index: number }) => ({
      onDelete: () => handleTagDelete(index),
    }),
    [handleTagDelete]
  );

  const trigger = useMemo(
    () => (
      <TextInput
        aria-label={filterValueLabel}
        sx={{
          maxWidth: 'none ',
          width: size === 'small' || !isVertical ? '406px' : '182px',
          whiteSpace: 'nowrap',
          '.MuiOutlinedInput-root': { gap: 0.5, pl: 1, cursor: 'pointer' },
          input: { visibility: 'hidden' },
          '.MuiChip-root': {
            height: size === 'xSmall' ? '18px' : '22px',
            '.MuiChip-label': {
              fontSize: size === 'xSmall' ? '12px' : '14px',
            },
          },
        }}
        /* eslint-disable-next-line react/no-unstable-nested-components */
        startAdornment={() =>
          rawExpression && rawExpression?.and?.[0]?.operator !== 'in' ? (
            rawExpression.type && isTopKFilter(rawExpression.type) ? (
              <TopKDisplay
                metric={rawExpression.value}
                top={rawExpression.top}
                dir={rawExpression.dir}
                style={{ ...ellipsis, width: 'calc(100% - 18px)' }}
              />
            ) : (
              <FieldFilterBoxSelectExpression
                rawExpression={rawExpression}
                key={(rawExpression.and || (rawExpression as any).or)?.length || 0}
              />
            )
          ) : selectedFilterOptions.length > 0 ? (
            <MultiSelectTags
              tags={selectedFilterOptions}
              getTagProps={getTagProps}
              size={size}
              getLabelFunc={({ label }: any) => label}
              getValueFunc={({ value }: any) => value}
            />
          ) : (
            'Select value'
          )
        }
        actionIcons={[<ArrowDown style={isOpen ? { transform: 'rotate(180deg)' } : {}} />]}
      />
    ),
    [size, value, rawExpression, selectedFilterOptions, getTagProps, filterValueLabel]
  );

  return (
    <FieldFilterBase
      filters={{ [fieldName!]: filters }}
      field={fieldName ?? ''}
      onOpen={() => setIsOpen(true)}
      onClose={() => setIsOpen(false)}
      isEnabled={!disabled && !!fieldName}
      onChange={noop}
      onFilterChange={onChange}
      trigger={trigger}
      disablePortalInOperatorSelect={false}
      extraOptions={extraOptions}
    />
  );
};
